import {
  type LotteryId,
  SubscriptionType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { EDITOR_TICKET_TYPE } from '#editor/app/constants/options'
import { useEditorOptionsStore } from '#editor/app/stores/options'
import { useProductStore } from '#product/app/stores/product'

export const useEditorStorePlaySlip = (
  lotteryId: LotteryId,
  { config, playInputs, selectedAddOns } = useEditorPrice(lotteryId),
) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const { addToast } = useToaster()
  const { t } = useNuxtApp().$i18n

  const optionsStore = useEditorOptionsStore(lotteryId)

  const playStore = useEditorPlayStore(lotteryId)

  const subscriptionOptions = () => {
    switch (optionsStore.ticketType) {
      case EDITOR_TICKET_TYPE.PrepaidSubscription:
        return {
          drawsPerPurchase: optionsStore.prepaidSubscriptionAmount?.value || 1,
          type: SubscriptionType.Prepaid,
        }
      case EDITOR_TICKET_TYPE.Subscription:
        return { drawsPerPurchase: 1, type: SubscriptionType.Renewing }
      default:
        return undefined
    }
  }

  const storePlaySlip = async () => {
    const currentConfig = config.value
    if (!currentConfig) throw new Error('lottery config not loaded')

    // reset or repair a slip persisted under an older config before it is sent with the current one
    playStore.setUp(currentConfig)
    optionsStore.setUp(currentConfig)

    const { mutate } = useStorePlaySlipMutation()

    try {
      const result = await mutate({
        input: {
          addOns: selectedAddOns.value,
          bonusCode: useHypeStore().couponCode,
          configVersion: currentConfig.version,
          licensedTerritory,
          lotteryId,
          numberOfDraws: currentConfig.numberOfDraws[0],
          plays: playInputs.value,
          subscriptionOptions: subscriptionOptions(),
        },
      })
      if (!result?.data) throw new Error('play slip not stored')

      useProductStore().setProduct(
        result.data.storePlaySlip.id,
        lotteryId.toLowerCase(),
        ProductType.Lottery,
      )
    } catch (error) {
      if (isSpecificGraphQLError(error, { matchMessage: 'validation.subscription.notallowed' })) {
        optionsStore.setTicketType(EDITOR_TICKET_TYPE.Single)
        addToast({
          autoClose: false,
          headline: t('toast.prepaidsubscription.notallowed.headline'),
          message: t('toast.prepaidsubscription.notallowed.message'),
          tracking: { topicKey: 'prepaid-subscription-not-allowed' },
          type: 'info',
        })
      }

      throw error
    }
  }

  return { storePlaySlip }
}
